/**
 * BridgeRuntime — implements IAgentRuntime from @skaile/workspaces/types by wrapping
 * an AgentDriver + EventNormalizer. This is the primary integration point for
 * consumers that want normalized events from any driver.
 */

import type {
  AgentEvent,
  AgentPromptInput,
  AgentReconfigureOptions,
  AgentRuntimeConfig,
  IAgentRuntime,
} from "@skaile/workspaces/types";

import { EventNormalizer } from "./normalizer.js";
import { createDriver } from "./registry.js";
import type { AgentConfig, AgentDriver } from "./types.js";

/**
 * Implements `IAgentRuntime` from `@skaile/workspaces/types` by wrapping an {@link AgentDriver}
 * and an {@link EventNormalizer}.
 *
 * This is the primary integration point for consumers that want normalized `AgentEvent`
 * payloads from any driver without managing the driver lifecycle directly. Create via
 * {@link createRuntime} to avoid coupling to `BridgeRuntime` directly.
 *
 * @docLink packages/bridge/api-reference#runtime
 */
export class BridgeRuntime implements IAgentRuntime {
  private driver: AgentDriver | null = null;
  private normalizer = new EventNormalizer();
  private handlers = new Set<(event: AgentEvent) => void>();
  private boundEventHandler: ((event: any) => void) | null = null;
  private driverId: string;

  constructor(driverId: string = "claude-sdk") {
    this.driverId = driverId;
  }

  async start(config: AgentRuntimeConfig): Promise<void> {
    // Dispose previous driver if any
    if (this.driver) {
      await this.dispose();
    }

    this.normalizer.reset();

    const bridgeConfig: AgentConfig = {
      cwd: config.cwd,
      provider: config.provider,
      model: config.model,
      systemPrompt: config.systemPrompt,
      apiKeys: config.apiKeys,
      env: config.env,
      agentDir: config.agentDir,
      sessionId: config.sessionId,
      resumeSessionId: config.resumeSessionId,
      maxTurns: config.maxTurns,
    };

    this.driver = await createDriver(this.driverId, bridgeConfig);

    // Wire bridge events through normalizer to handlers
    this.boundEventHandler = (bridgeEvent: any) => {
      const normalized = this.normalizer.normalize(bridgeEvent);
      for (const event of normalized) {
        for (const handler of this.handlers) {
          handler(event);
        }
      }
    };
    this.driver.on("agent-event", this.boundEventHandler);

    await this.driver.start();
  }

  async configure(_config: AgentReconfigureOptions): Promise<void> {
    // Runtime reconfiguration for BridgeRuntime is a no-op for now.
    // Future: reconnect connectors, reload ai-assets, update subagents.
  }

  async prompt(message: string): Promise<void> {
    if (!this.driver) throw new Error("Runtime not started — call start() first");
    await this.driver.prompt(message);
  }

  async promptInput(input: AgentPromptInput): Promise<void> {
    if (!this.driver) throw new Error("Runtime not started — call start() first");
    await this.driver.promptInput(input);
  }

  async reply(answer: string, question?: string, requestId?: string): Promise<void> {
    if (!this.driver) throw new Error("Runtime not started — call start() first");
    // Mirror serve.ts handleReply: an answer must resume the paused turn. Falling through to
    // prompt() would deny the question as superseded and drop the structured answer.
    if (
      this.driver?.hasPendingQuestion() &&
      this.driver.answerQuestion(answer, question, requestId)
    )
      return;
    if (this.driver?.acceptsReplyAsPrompt) await this.prompt(answer);
  }

  async abort(): Promise<void> {
    if (this.driver) {
      await this.driver.abort();
    }
  }

  async dispose(): Promise<void> {
    if (this.driver) {
      if (this.boundEventHandler) {
        this.driver.off("agent-event", this.boundEventHandler);
        this.boundEventHandler = null;
      }
      this.driver.kill();
      this.driver = null;
    }
    this.normalizer.reset();
  }

  onEvent(handler: (event: AgentEvent) => void): void {
    this.handlers.add(handler);
  }

  offEvent(handler: (event: AgentEvent) => void): void {
    this.handlers.delete(handler);
  }

  get isRunning(): boolean {
    return this.driver?.isRunning ?? false;
  }
}

/**
 * Factory function to create a {@link BridgeRuntime} with the specified driver.
 *
 * @param driverId - Registered driver id (default: `"claude-sdk"`).
 * @returns A new `IAgentRuntime` wrapping the selected driver with event normalization.
 * @docLink packages/bridge/api-reference#runtime
 */
export function createRuntime(driverId: string = "claude-sdk"): IAgentRuntime {
  return new BridgeRuntime(driverId);
}
